import { useCallback, useEffect, useState } from 'react';
import { mentionedAddresses, type Comment, type CommentSubjectKind } from '@reef/schema';
import { request } from '../http.ts';

/**
 * The conversation about one thing.
 *
 * Deliberately addressed by `subjectKind` plus `subjectId` and nothing else, so
 * the same component hangs off a run, a step, or an artifact without knowing
 * what any of those are. That is why it fetches its own thread rather than
 * taking one as a prop: a parent that had to load comments before it could
 * render would make every screen that wants a thread also want the API for one.
 *
 * The thread is flat and oldest-first because it is read as a narrative of what
 * people worked out. Newest-first would put the conclusion above the evidence.
 */
export function CommentThread({
  subjectKind,
  subjectId,
}: {
  subjectKind: CommentSubjectKind;
  subjectId: string;
}) {
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    collaborate
      .listComments(subjectKind, subjectId)
      .then((result) => setComments(result.comments))
      .catch((caught) => setError((caught as Error).message));
  }, [subjectKind, subjectId]);

  useEffect(refresh, [refresh]);

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const post = () => {
    const body = draft.trim();
    if (!body) return;
    return act(async () => {
      await collaborate.addComment({ subjectKind, subjectId, body });
      setDraft('');
    });
  };

  return (
    <div className="stack">
      <p className="section-title" style={{ margin: 0 }}>
        Discussion
      </p>

      {error && (
        <div className="banner danger">
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {comments === null && <p className="hint">Loading…</p>}

      {comments?.length === 0 && (
        <div className="empty">
          Nothing said yet. Mention someone by their address — <code>@dana@acme.com</code> — and
          Reef delivers it to that account.
        </div>
      )}

      {comments?.map((comment) => (
        <div className="card" key={comment.id}>
          <div className="row wrap">
            <strong style={{ fontSize: '0.85rem' }}>{comment.authorEmail ?? comment.authorId}</strong>
            <span className="hint">{new Date(comment.createdAt).toLocaleString()}</span>
            {/* A reply is shown as a reply rather than indented, because one
                level of nesting is not a tree and rendering it as one implies
                depths that cannot exist. */}
            {comment.parentId && <span className="pill">reply</span>}
            {comment.resolvedAt && <span className="pill ok">resolved</span>}
          </div>

          <p style={{ margin: '0.4rem 0 0.6rem', whiteSpace: 'pre-wrap' }}>
            {renderBody(comment.body)}
          </p>

          <div className="row wrap">
            {!comment.resolvedAt && (
              <button
                disabled={busy}
                onClick={() => act(() => collaborate.resolveComment(comment.id))}
              >
                Resolve
              </button>
            )}
            {/*
              Offered to everyone and refused by the API for anybody but the
              author, which is the honest ordering: this screen does not know
              who you are, and a control hidden on a guess is a control that
              disappears for the person who should have it.
            */}
            <button disabled={busy} onClick={() => act(() => collaborate.deleteComment(comment.id))}>
              Delete
            </button>
          </div>
        </div>
      ))}

      <div className="stack">
        <textarea
          rows={3}
          value={draft}
          placeholder="Say something about this…"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void post();
            }
          }}
        />
        <div className="row">
          <span className="hint">⌘↵ to post</span>
          <button className="primary" disabled={busy || !draft.trim()} onClick={() => void post()}>
            Comment
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Render a body with its mentions picked out.
 *
 * Which substrings *are* mentions is asked of `@reef/schema` rather than
 * decided here, because the server resolves and delivers on that same function.
 * A second regex in the browser is a second opinion, and the one people would
 * trust is whichever one they can see — so a mention highlighted here but not
 * delivered there would look like reef losing a message.
 */
function renderBody(body: string) {
  const addresses = mentionedAddresses(body);
  if (addresses.length === 0) return body;

  const pattern = new RegExp(`(@(?:${addresses.map(escapeRegExp).join('|')}))`, 'gi');
  return body.split(pattern).map((piece, index) =>
    // `split` on a capturing pattern alternates literal, capture, literal — so
    // the odd indices are exactly the mentions and no second match is needed.
    index % 2 === 1 ? <strong key={index}>{piece}</strong> : <span key={index}>{piece}</span>,
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * A local client for the two collaboration endpoints.
 *
 * Kept here rather than in `api.ts` because these calls have exactly one caller
 * — this component — and a shared client accumulates methods that only one
 * screen ever uses. The session cookie is what authorizes them, hence
 * `credentials: 'include'` on every call.
 */
const collaborate = {
  listComments: (subjectKind: string, subjectId: string) =>
    request<{ comments: Comment[] }>(
      `/collaborate/comments?subjectKind=${encodeURIComponent(subjectKind)}&subjectId=${encodeURIComponent(subjectId)}`,
    ),

  addComment: (body: { subjectKind: string; subjectId: string; body: string; parentId?: string }) =>
    request<{ comment: Comment }>('/collaborate/comments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  resolveComment: (id: string) =>
    request<{ resolved: boolean }>(`/collaborate/comments/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
    }),

  deleteComment: (id: string) =>
    request<{ deleted: boolean }>(`/collaborate/comments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

